import { Button } from '@eventbrite/eds-button';
import { gettext } from '@eventbrite/i18n';
import React from 'react';
import {
    CTA_CHECKOUT_TEXT,
    CTA_COMPLETE_FREE_TEXT,
    CTA_EXPRESS_CHECKOUT_WITH_APPLE_PAY,
} from '../../constants';
import { ApplePayNameSpace } from '../../state/applePay';
import { isExpressCheckoutCtaCopyVariant } from '../../utils/statsig';
import { ConnectedApplepayNativeButton } from '../paymentMethodForms/ApplepayNativeButton/ConnectedApplepayNativeButton';
import { getEventbriteUrl } from '../utils';
import './ExpressCheckout.scss';

export enum StatsigValueType {
    APPLE_PAY = 'Express Checkout - Apple pay',
    DEFAULT_CTA = 'Express Checkout - Default CTA',
}

export type ExpressCheckoutSubmitDataType = {
    isApplePay?: boolean;
    statsigValue: StatsigValueType;
};

const CTACopyExperiment = isExpressCheckoutCtaCopyVariant()
    ? CTA_CHECKOUT_TEXT
    : CTA_EXPRESS_CHECKOUT_WITH_APPLE_PAY;

export type ExpressCheckoutProps = {
    applePay: ApplePayNameSpace;
    handleSubmit: (props: ExpressCheckoutSubmitDataType) => void;
    isFreeOrder: boolean;
    isSubmitting: boolean;
    totalTicketQuantity: number;
    locale: string;
    isExpressCheckout: boolean;
};

export const SkeletonButton = () => (
    <div className="skeleton-button" data-testid="skeleton-button">
        <span className="eds-is-hidden-accessible">
            {gettext('Loading...')}
        </span>
    </div>
);

export const LegalText = ({ locale }: { locale: string }) => {
    const urlPrefix = getEventbriteUrl(locale);

    const tosLink = `<a href="${urlPrefix}/tos" class="eds-link" target="_blank" id="tos" rel="noopener noreferrer">${gettext(
        'Eventbrite Terms of Service',
    )}</a>`;

    const TOS_TEXT = gettext(
        'By clicking Apple Pay, I agree to the %(tosLink)s.',
        { tosLink },
    ).toString();

    return (
        <p
            className="express-checkout-legal-text eds-text-color--ui-800"
            data-testid="express-checkout-legal-text"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
                __html: TOS_TEXT,
            }}
        />
    );
};

export const ExpressCheckout: React.FC<ExpressCheckoutProps> = ({
    applePay,
    handleSubmit,
    isFreeOrder,
    isSubmitting,
    locale,
    totalTicketQuantity,
}) => {
    const { applepayInstance, hasAsyncErrors } = applePay;
    const areOnlyFreeTicketsSelected = isFreeOrder && totalTicketQuantity > 0;
    const shouldShowApplePayButton =
        !hasAsyncErrors && !areOnlyFreeTicketsSelected;
    const shouldShowCtaCheckoutText =
        !applepayInstance && !areOnlyFreeTicketsSelected;
    const shouldShowCtaExpressCheckoutText =
        applepayInstance && !areOnlyFreeTicketsSelected;
    const shouldShowLegalText = applepayInstance && !isFreeOrder;

    return (
        <>
            {shouldShowLegalText && (
                <div className="eds-l-pad-bot-5 eds-fx--fade-in eds-fx--duration-2">
                    <LegalText locale={locale} />
                </div>
            )}

            <div
                data-testid="express-checkout"
                className="eds-align--right eds-fx--fade-in eds-fx--duration-2"
            >
                <div className="express-checkout-buttons">
                    {shouldShowApplePayButton && (
                        <div className="express-checkout-single-button">
                            {applepayInstance ? (
                                <div className="eds-fx--fade-in eds-fx--duration-2">
                                    <ConnectedApplepayNativeButton
                                        handleSubmit={handleSubmit}
                                    />
                                </div>
                            ) : (
                                <SkeletonButton />
                            )}
                        </div>
                    )}
                    <div className="express-checkout-single-button">
                        <Button
                            onClick={() =>
                                handleSubmit({
                                    statsigValue: StatsigValueType.DEFAULT_CTA,
                                })
                            }
                            style="fill"
                            size="block"
                            type="button"
                            data-spec={'eds-modal__primary-button'}
                            data-automation={'eds-modal__primary-button'}
                            isLoading={isSubmitting}
                        >
                            {areOnlyFreeTicketsSelected &&
                                CTA_COMPLETE_FREE_TEXT}
                            {shouldShowCtaExpressCheckoutText &&
                                CTACopyExperiment}
                            {shouldShowCtaCheckoutText && CTA_CHECKOUT_TEXT}
                        </Button>
                    </div>
                </div>
            </div>
        </>
    );
};
